import { Box, Paper } from '@mui/material'
import { calculateGeographicDistance } from '../../common/utils/geographic-utils'
import { useMeasurementUnits } from '~/common/use-measurement-units'

/**
 * SVG overlay that displays north-south (red) and east-west (blue) dashed distance lines
 * Forms an L-shaped vector addition pattern from last point to cursor position
 * - Horizontal (blue): Shows X displacement from last point
 * - Vertical (red): Shows Y displacement from end of horizontal line
 * Points are in CRS.Simple pixel coordinates {x, y}
 * Screen positions are in pixels relative to map container
 */
export const DistanceMeasurementLines = ({
  lastPoint,
  cursorPosition,
  lastPointScreenPos,
  cursorScreenPos,
  climateBounds,
}) => {
  const { formatDistance } = useMeasurementUnits()

  if (!lastPoint || !cursorPosition || !lastPointScreenPos || !cursorScreenPos) {
    return null
  }

  // Calculate distances for each axis
  const horizontalPoint = { x: cursorPosition.x, y: lastPoint.y }
  const verticalPoint = { x: lastPoint.x, y: cursorPosition.y }

  const eastWestDistance = calculateGeographicDistance(lastPoint, horizontalPoint, climateBounds, 2000)
  const northSouthDistance = calculateGeographicDistance(lastPoint, verticalPoint, climateBounds, 2000)

  // Screen coordinates for L-shaped measurement lines (vector addition)
  // Horizontal line endpoint (shows X displacement)
  const horizontalEndX = cursorScreenPos.x
  const horizontalEndY = lastPointScreenPos.y

  return (
    <>
      {/* SVG lines */}
      <Box
        component="svg"
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 999,
        }}
      >
        {/* East-West line (horizontal, blue) - X displacement from last point */}
        <line
          x1={lastPointScreenPos.x}
          y1={lastPointScreenPos.y}
          x2={horizontalEndX}
          y2={horizontalEndY}
          stroke="#2196f3"
          strokeWidth="3"
          strokeDasharray="8 4"
        />

        {/* North-South line (vertical, red) - Y displacement from end of horizontal */}
        <line
          x1={horizontalEndX}
          y1={horizontalEndY}
          x2={cursorScreenPos.x}
          y2={cursorScreenPos.y}
          stroke="#f44336"
          strokeWidth="3"
          strokeDasharray="8 4"
        />
      </Box>

      {/* East-West distance label (on horizontal line) */}
      {eastWestDistance > 0 && (
        <Paper
          elevation={1}
          sx={{
            position: 'absolute',
            left: (lastPointScreenPos.x + horizontalEndX) / 2,
            top: horizontalEndY - 9,
            transform: 'translateX(-50%)',
            px: 0.75,
            borderRadius: 0.75,
            pointerEvents: 'none',
            zIndex: 1000,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: '#2196f3',
          }}
        >
          <Box
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.6rem',
              fontWeight: 400,
              color: '#2196f3',
            }}
          >
            {formatDistance(eastWestDistance)}
          </Box>
        </Paper>
      )}

      {/* North-South distance label (on vertical line) */}
      {northSouthDistance > 0 && (
        <Paper
          elevation={1}
          sx={{
            position: 'absolute',
            left: cursorScreenPos.x + 4,
            top: (horizontalEndY + cursorScreenPos.y) / 2,
            transform: 'translateY(-50%)',
            px: 0.75,
            borderRadius: 0.75,
            pointerEvents: 'none',
            zIndex: 1000,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: '#f44336',
          }}
        >
          <Box
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.6rem',
              fontWeight: 400,
              color: '#f44336',
            }}
          >
            {formatDistance(northSouthDistance)}
          </Box>
        </Paper>
      )}
    </>
  )
}
